import {
  Component, ElementRef, OnDestroy, OnInit,
  ViewChild,
} from '@angular/core';
import {ProjectService} from '@catcore/services';
import {DiagramService} from '@catcore/services/diagram/diagram.service';
import {GenericLinkedResource} from '@catcore/types/base';
import {SBDService} from '@catdetails/services';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {ModifyUnitComponent} from '@catmodules/models/units/components/modify-unit/modify-unit.component';
import {ModalService} from '@catmodules/widgets/slide-in-modal/services/modal.service';
import {Subscription} from 'rxjs/Subscription';
import 'rxjs/add/operator/takeUntil';
import {isNullOrUndefined} from 'util';

declare let SystemBlockDiagram: any;

import * as d3 from 'd3';
import '../../../../lib/blockdiagram-editor/system-block-diagram.js';
import {SvgExportService} from "@catcore/services/svg-export/svg-export.service";
import {DisplayTypes} from "@catui/components/panel/types";
import {ModifyPortConnectionComponent} from "@catmodules/models/feature-port/components/modify-port-connection/modify-port-connection.component";
import {Subject} from "rxjs/Subject";
import {ModifyInputChannelsComponent} from "@catmodels/project-speaker-setup/components/modify-input-channels/modify-input-channels.component";

@Component({
  selector: 'catapp-project-diagram',
  templateUrl: './project-diagram.component.html',
  styleUrls: ['./project-diagram.component.scss'],
})
export class ProjectDiagramComponent implements OnInit, OnDestroy {

  projectDiagram = null;
  sbd = null;
  panelDisplayType = DisplayTypes;
  autoLayoutPorts = true;
  panEnabled = false;
  showGuiZones = true;
  sbdLoaded = false;
  sbdDirty = false;
  displayExportDialog = false;

  private componetDestroyed: Subject<any> = new Subject();

  exportOrientation = SvgExportService.ORIENTATION_LANDSCAPE;
  exportOrientationOptions = [
    { value: SvgExportService.ORIENTATION_LANDSCAPE, label: 'Landscape' },
    { value: SvgExportService.ORIENTATION_PORTRAIT, label: 'Portrait' },
  ];

  exportPaperSize = SvgExportService.PAPERSIZE_A4;
  exportPaperSizeOptions = [
    { value: SvgExportService.PAPERSIZE_A4, label: 'A4' },
    { value: SvgExportService.PAPERSIZE_A3, label: 'A3' },
  ];

  exportCompression = SvgExportService.COMPRESSION_FAST;

  exportScaleToFit = true;
  exportScaleToFitOptions = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  project = null;
  private subscription: Subscription;

  @ViewChild('svgDiv')
  private svgDiv: ElementRef;

  @ViewChild('canvas')
  private canvas: ElementRef;

  @ViewChild('pngContainer')
  private pngContainer: ElementRef;

  private unitSubscription: Subscription;
  private guiZones = [];

  constructor(private projectService: ProjectService,
              private diagramService: DiagramService,
              private genericResourceService: GenericResourceService,
              private sbdRendererService: SBDService,
              private modalService: ModalService,
              private svgExportService: SvgExportService) {
  }

  ngOnInit() {
    this.subscription = this.projectService
      .getCurrentProject()
      .subscribe((project) => {
        this.project = project;
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.guiZones = this.projectService.extractGuiZonesFrom(this.project)
              .filter((zone) => zone.type === 'zone');
          this.refreshDiagram();
          this.sbdRendererService.setActive();
        }
      });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.sbdRendererService.setInActive();

    this.componetDestroyed.next();
    this.componetDestroyed.unsubscribe();
  }

  toggleAutoLayoutPorts() {
    this.autoLayoutPorts = !this.autoLayoutPorts;
    if (this.sbd) {
      this.sbd.enableAutoSetPortLocations(this.autoLayoutPorts);
    }
  }

  togglePanEnabled() {
    this.panEnabled = !this.panEnabled;
    if (this.sbd) {
      this.sbd.setPanEnabled(this.panEnabled);
    }
  }

  toggleShowGuiZones() {
    this.showGuiZones = !this.showGuiZones;
    if (this.sbd) {
      this.sbd.showGuiZones(this.showGuiZones);
    }
  }

  zoomOut() {
    if (this.sbd) {
      this.sbd.zoomOut();
    }
  }

  zoomIn() {
    if (this.sbd) {
      this.sbd.zoomIn();
    }
  }

  updateDiagram() {
    while (this.svgDiv.nativeElement.firstChild) {
      this.svgDiv.nativeElement.removeChild(this.svgDiv.nativeElement.firstChild);
    }
    this.sbd.render(this.svgDiv.nativeElement);
    this.sbd.resizeSvgToContent();
    this.sbd.enableAutoSetPortLocations(this.autoLayoutPorts);
    this.sbd.setPanEnabled(this.panEnabled);
    this.sbd.showGuiZones(this.showGuiZones);
  }

  refreshDiagram() {
    this.sbdLoaded = false;
    const isValidProject = !isNullOrUndefined(this.project) && !isNullOrUndefined(this.project.id);
    if (isValidProject) {
      this.projectService
        .getBlockDiagram(this.project)
        .takeUntil(this.componetDestroyed)
        .subscribe((blockDiagram: any) => {
          this.projectDiagram = blockDiagram;

          this.sbd = new SystemBlockDiagram(d3);
          this.sbd.renderMenu = false;

          this.sbd.onSetDirty = () => { this.sbdDirty = true; };
          this.sbd.onSave = () => { this.save(); };
          this.sbd.onUnitDblClick = (unit) => { this.onUnitDblClick(unit); };
          this.sbd.onPortDblClick = (port) => { this.onPortDblClick(port); };
          this.sbd.onPortConnectionDblClick = (pc) => { this.onPortConnectionDblClick(pc); };

          this.sbd.setDiagramData(this.projectDiagram);

          this.updateDiagram();

          this.sbdLoaded = true;
        });
    }
  }

  sbdSave() {
    if (this.sbd) {
      this.sbd.save();
    }
  }

  save() {
    this.diagramService
      .update(this.projectDiagram)
      .subscribe((updates) => {
        this.sbdDirty = false;
      });
  }

  setExportOrientation(value) {
    this.exportOrientation = value;
  }

  setExportPaperSize(value) {
    this.exportPaperSize = value;
  }

  setExportScaleToFit(value)  {
    this.exportScaleToFit = value;
  }

  /**
   * Exports the diagram as pdf by converting it to a png first and then
   * using jsPDF for the pdf generation.
   */
  exportDiagram() {
    this.displayExportDialog = true;
  }

  exportDiagramToPDF() {
    const svgElem = this.svgDiv.nativeElement.firstChild;
    const pngContainerDivElem = this.pngContainer.nativeElement;
    const canvasElem = this.canvas.nativeElement;

    const title = this.project.name;
    const subtitle = 'System Block Diagram';
    const filename = this.project.name + ' - SBD.pdf';

    this.svgExportService.exportSvgToPdf(svgElem,
      canvasElem,
      pngContainerDivElem,
      title,
      subtitle,
      filename,
      this.exportOrientation,
      this.exportPaperSize,
      this.exportScaleToFit,
      this.exportCompression);

    this.displayExportDialog = false;
  }

  /**
   * Raw event callback to handle a doubleclick event
   * @param diagramUnit
   */
  onUnitDblClick(diagramUnit) {
    const unitResource = diagramUnit.source._links.self;
    this.openModifyUnitDialog(unitResource, () => {
      const service = this.genericResourceService.findServiceForResource(diagramUnit._links.self.href);
      service.getCurrent()
        .takeUntil(this.componetDestroyed)
        .subscribe((updatedUnit) => {
        if (!isNullOrUndefined(updatedUnit)) {
          let diagramUnits = this.projectDiagram._embedded.diagram_units;
          for (let i = 0; i < diagramUnits.length; i++) {
            if (diagramUnits[i]._links.self.href === diagramUnit._links.self.href) {
              diagramUnits[i] = Object.assign(diagramUnits[i], updatedUnit);
              break;
            }
          }
          this.updateDiagram();
        }
      });
      service.load();
    });
  }

  /**
   * Raw event callback to handle a doubleclick event
   * @param portConnection
   */
  onPortConnectionDblClick(portConnection) {
    if (portConnection.editable) {
      this.openModifyPortConnectionDialog(portConnection.source._links.self,
        () => {
          const service = this.genericResourceService.findServiceForResource(portConnection._links.self.href);
          // Remove diagram port connection so that it is updated on diagram refresh
          service.delete().takeUntil(this.componetDestroyed).subscribe(() => {
            this.refreshDiagram();
          });
        });
    }
  }

  onPortDblClick(port) {
    console.log('Clicked Port:', port);
    switch (port.parent.partnumber.substr(0, 3)) {
      case 'SPK':
      case 'SOB':
      case 'SUB':
        this.openModifySpeakerPortDialog(port.source._links.self, () => {});
        break;
      default:
    }
  }

  private openModifyUnitDialog(unitResource: GenericLinkedResource, afterUpdate) {
    this.openDialog(unitResource,
      (unit) => this.unitModalFactory(unit),
      afterUpdate);
  }

  private openModifyPortConnectionDialog(portConnectionResource: GenericLinkedResource, afterUpdate) {
    this.openDialog(portConnectionResource,
      (portConnection) => this.portConnectionModalFactory(portConnection),
      afterUpdate);
  }

  private openModifySpeakerPortDialog(speakerPortResource: GenericLinkedResource, afterUpdate) {
    this.openDialog(speakerPortResource,
      (speakerPort) =>  this.speakerPortModalFactory(speakerPort),
      afterUpdate);
  }

  private portConnectionModalFactory(portConnection) {
    const compRef = this.modalService.open(ModifyPortConnectionComponent);
    compRef.componentInstance.title = `Modify Port Connection`;
    compRef.componentInstance.featureLevel = false;
    compRef.componentInstance.displayDelete = false;
    compRef.componentInstance.model = portConnection;
    compRef.componentInstance.afterUpdate.subscribe(() => {});
    return compRef;
  }

  private unitModalFactory(unit) {
    const compRef = this.modalService.open(ModifyUnitComponent);
    compRef.componentInstance.unit = unit;
    compRef.componentInstance.enableDelete = false;
    compRef.componentInstance.afterUpdate.subscribe(() => {});
    return compRef;
  }

  private speakerPortModalFactory(speakerPort) {
    const compRef = this.modalService.open(ModifyInputChannelsComponent);
    compRef.componentInstance.guiZones = this.guiZones;
    compRef.componentInstance.speakerPort = speakerPort;
    compRef.componentInstance.afterUpdate.subscribe(() => {});
    return compRef;
  }

  private openDialog(resource: GenericLinkedResource, componentFactory, afterUpdate = null) {
    let compRef;
    const onFullfill = () => {
      if (afterUpdate) {
        afterUpdate();
      }
      resourceHandler.refresh();
      this.unitSubscription.unsubscribe();
    };

    const onError = () => {
      this.unitSubscription.unsubscribe();
    };

    const resourceHandler = this
      .genericResourceService
      .findServiceForResource(resource.href);

    this.unitSubscription = resourceHandler
      .loadAndGet()
      .subscribe((item) => {
        if (compRef) {
          compRef.close();
          compRef = null;
        }

        if (isNullOrUndefined(item)) {
          compRef = null;
        } else {
          compRef = componentFactory(item);
          compRef.result.then(onFullfill, onError);
        }
      });
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_master@2/app/Resources/Public/src/app/modules/project-details/components/project-diagram/project-diagram.component.ts